import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import apiClient, { setRefreshTokenFunction } from '../api/client';
import { clearSession, getCachedSessionToken, getSessionToken, isTransientSessionError, onSessionCleared, setSessionToken } from '../api/session';

const AuthContext = createContext();
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const defaultRoute = (user) => ['admin', 'worker'].includes(user?.role) ? '/dashboard' : user?.role === 'investor' ? '/owner' : '/login';

const expiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!Number.isFinite(payload.exp)) throw new Error('Missing session expiration.');
    return payload.exp * 1000;
  } catch {
    throw new Error('The server returned an invalid session token.');
  }
};

const validateIdentity = (identity) => {
  if (!identity || !['admin', 'worker', 'investor'].includes(identity.role) || !identity.username ||
      (identity.role === 'investor' && !identity.investor_id)) {
    throw new Error('The server returned an unsupported account identity.');
  }
  return { username: identity.username, name: identity.name, role: identity.role, investor_id: identity.investor_id };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionMessage, setSessionMessage] = useState('');
  const [sessionId, setSessionId] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const timer = useRef(null);
  const refreshing = useRef(null);
  const generation = useRef(0);
  const refreshRef = useRef(null);
  const identityRef = useRef(null);

  const applyIdentity = useCallback((identity) => {
    const previous = identityRef.current;
    if (previous && (previous.role !== identity.role || previous.username !== identity.username ||
        previous.investor_id !== identity.investor_id)) setSessionId((value) => value + 1);
    identityRef.current = identity;
    setUser(identity);
  }, []);

  const scheduleRefresh = useCallback((token) => {
    clearTimeout(timer.current);
    const remaining = expiry(token) - Date.now();
    if (remaining <= 0) { clearSession('Your session expired. Please sign in again.'); return; }
    timer.current = setTimeout(() => {
      // refreshToken handles session invalidation and transient retries.
      refreshRef.current().catch(() => {});
    }, Math.max(1000, remaining - Math.min(5 * 60 * 1000, remaining / 2)));
  }, []);

  const refreshToken = useCallback(() => {
    if (refreshing.current?.generation === generation.current) return refreshing.current.promise;
    const token = getSessionToken();
    if (!token || expiry(token) <= Date.now()) {
      clearSession('Your session expired. Please sign in again.');
      return Promise.reject(new Error('Your session expired. Please sign in again.'));
    }
    const requestGeneration = generation.current;
    const pending = (async () => {
      const result = await apiClient.request('/api/auth/refresh', {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      if (requestGeneration !== generation.current) throw new DOMException('Session changed.', 'AbortError');
      if (!result.token || expiry(result.token) <= Date.now()) throw new Error('Session refresh failed. Please sign in again.');
      setSessionToken(result.token);
      const identity = validateIdentity(await apiClient.request('/api/auth/me', {
        headers: { Authorization: `Bearer ${result.token}` }, sessionIndependent: true,
      }));
      if (requestGeneration !== generation.current) throw new DOMException('Session changed.', 'AbortError');
      if (expiry(result.token) <= Date.now()) throw new Error('Your session expired. Please sign in again.');
      applyIdentity(identity);
      scheduleRefresh(result.token);
      return result.token;
    })().catch((error) => {
      if (requestGeneration === generation.current && error.name !== 'AbortError') {
        const currentToken = getSessionToken();
        if (isTransientSessionError(error) && currentToken && expiry(currentToken) > Date.now()) {
          console.warn('Session refresh temporarily unavailable; retrying:', error.message);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => refreshRef.current().catch(() => {}),
            Math.min(30000, expiry(currentToken) - Date.now()));
        } else {
          clearSession(error.message);
        }
      }
      throw error;
    }).finally(() => {
      if (refreshing.current?.promise === pending) refreshing.current = null;
    });
    refreshing.current = { generation: requestGeneration, promise: pending };
    return pending;
  }, [applyIdentity, scheduleRefresh]);
  refreshRef.current = refreshToken;

  useEffect(() => {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    } catch (error) {
      console.warn('Unable to remove legacy credentials:', error.message);
    }
    const unsubscribe = onSessionCleared((message) => {
      generation.current += 1;
      clearTimeout(timer.current);
      setUser(null);
      identityRef.current = null;
      setSessionId((value) => value + 1);
      setSessionMessage(message);
      setIsLoading(false);
    });
    setRefreshTokenFunction(refreshToken);
    const restoreGeneration = generation.current;
    const cachedToken = getCachedSessionToken();
    const restore = async () => {
      try {
        if (!cachedToken) return;
        if (expiry(cachedToken) <= Date.now()) throw new Error('Your session expired. Please sign in again.');
        const identity = validateIdentity(await apiClient.request('/api/auth/me', {
          headers: { Authorization: `Bearer ${cachedToken}` }, sessionIndependent: true,
        }));
        if (restoreGeneration !== generation.current) return;
        if (expiry(cachedToken) <= Date.now()) throw new Error('Your session expired. Please sign in again.');
        setSessionToken(cachedToken);
        applyIdentity(identity);
        setSessionMessage('');
        scheduleRefresh(cachedToken);
      } catch (error) {
        if (restoreGeneration !== generation.current) return;
        if (isTransientSessionError(error) && expiry(cachedToken) > Date.now()) {
          console.warn('Session verification temporarily unavailable:', error.message);
          setSessionMessage('Unable to verify your saved session. Check your connection; retrying automatically.');
          timer.current = setTimeout(restore, Math.min(30000, expiry(cachedToken) - Date.now()));
        } else {
          clearSession(error.message);
        }
      } finally {
        if (restoreGeneration === generation.current) setIsLoading(false);
      }
    };
    restore();
    return () => { generation.current += 1; unsubscribe(); clearTimeout(timer.current); setRefreshTokenFunction(null); };
  }, [applyIdentity, refreshToken, scheduleRefresh]);

  const logout = useCallback(async () => {
    const token = getSessionToken();
    clearSession();
    window.google?.accounts?.id?.disableAutoSelect();
    if (token) {
      try {
        await apiClient.request('/api/auth/logout', { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        console.warn('Server logout notification failed:', error.message);
      }
    }
  }, []);

  const establishSession = useCallback(async (response, requestGeneration, replaceSession = false) => {
    const identity = validateIdentity(response);
    if (!response.token || expiry(response.token) <= Date.now()) throw new Error('The server returned an expired session.');
    if (requestGeneration !== generation.current) throw new Error('Sign-in cancelled.');
    // Verify the candidate token without refreshing or clearing a different active session.
    const restored = validateIdentity(await apiClient.request('/api/auth/me', {
      headers: { Authorization: `Bearer ${response.token}` }, sessionIndependent: true,
    }));
    if (requestGeneration !== generation.current) throw new Error('Sign-in cancelled.');
    if (expiry(response.token) <= Date.now()) throw new Error('The server returned an expired session.');
    const verified = { ...restored, name: restored.name || identity.name };
    if (replaceSession) clearSession();
    else generation.current += 1;
    setSessionToken(response.token);
    applyIdentity(verified);
    scheduleRefresh(response.token);
    return { success: true, redirectTo: defaultRoute(verified) };
  }, [applyIdentity, scheduleRefresh]);

  const login = useCallback(async (username, password) => {
    clearSession();
    setIsLoading(true);
    const requestGeneration = generation.current;
    try {
      const response = await apiClient.request('/api/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }),
      });
      return await establishSession(response, requestGeneration);
    } catch (error) {
      if (requestGeneration === generation.current) clearSession();
      return { success: false, message: error.message || 'Unable to sign in.' };
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  const loginWithGoogle = useCallback(async (credential) => {
    const requestGeneration = generation.current;
    try {
      if (typeof credential !== 'string' || !credential) throw new Error('Google did not return an ID token. Please try again.');
      const response = await apiClient.request('/api/auth/google/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: credential }),
      });
      return await establishSession(response, requestGeneration, true);
    } catch (error) {
      const messages = {
        400: 'Google sign-in could not be processed. Please try again.',
        401: 'Google identity could not be verified. Please try again.',
        403: 'This Google account is not approved. Choose another Google account or use password login.',
        503: 'Google login is unavailable. Please try again later or use password login.',
      };
      return { success: false, message: messages[error.status] ||
        (error instanceof TypeError ? 'Google login is unavailable. Check your connection or use password login.' : error.message || 'Unable to sign in with Google.') };
    }
  }, [establishSession]);

  return <AuthContext.Provider value={{
    user, sessionId, sessionMessage, isLoading, login, loginWithGoogle, logout, refreshToken,
    isAdmin: () => user?.role === 'admin',
    isInvestor: () => user?.role === 'investor',
    isWorker: () => user?.role === 'worker',
    isGuest: () => !user,
    getDefaultRoute: () => defaultRoute(user),
  }}>{children}</AuthContext.Provider>;
};
